import 'driver.js/dist/driver.css';
import { type Theme, useTheme } from '@mui/material';
import { type Driver, type DriveStep, driver } from 'driver.js';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { renderPopover } from '/@/components/driverjs';
import useUnitProvider from '/@/core/context/unit';
import { useAllCatalogProducts, useCatalogProductsByBrands } from '/@/hooks/catalog';
import { useIsValidatedAdvisor, useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import type { WalkthroughMixpanelSection } from '/@/store/tracking/events/walkthrough';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import useIsPlaywriteEnvironment from '/@/utils/hooks/is-playwrite-environment';
import { reverse } from '/@/utils/router';
import { parseProductForProductCard } from '../components/product-card';
import { ALL_BRANDS_SLUG } from '../list';
import {
    PRODUCT_EXPLORATION_TOUR_IDS,
    PRODUCT_EXPLORATION_TOUR_RETURN_STORAGE_KEY,
    PRODUCT_EXPLORATION_TOUR_STORAGE_KEY,
} from './product-exploration-tour-ids';

const TOUR_SELECTORS = [
    `#${PRODUCT_EXPLORATION_TOUR_IDS.SEARCH}`,
    `#${PRODUCT_EXPLORATION_TOUR_IDS.CTA_GRID}`,
    `#${PRODUCT_EXPLORATION_TOUR_IDS.HYBRID_CARD}`,
    `#${PRODUCT_EXPLORATION_TOUR_IDS.HYBRID_TABS}`,
    `#${PRODUCT_EXPLORATION_TOUR_IDS.CP_CARD}`,
    `#${PRODUCT_EXPLORATION_TOUR_IDS.CP_TABS}`,
] as const;

const NAVIGATE_AFTER_STEP = new Set([1, 2, 3, 4]);
const TOTAL_STEPS = 6;
const MIXPANEL_SECTION: WalkthroughMixpanelSection = 'product-exploration';

type TourPaths = {
    landingPath: string;
    hybridListPath: string;
    hybridDetailPath: string;
    cpListPath: string;
    cpDetailPath: string;
    ready: boolean;
};

function buildDriveSteps(p: {
    stepCopy: { title: string; description: string }[];
    popoverTag: string;
    routeAfterIndex: Record<number, string>;
    routeBackFromStep: Record<number, string>;
    theme: Theme;
    navigate: ReturnType<typeof useNavigate>;
    chainingTourRef: MutableRefObject<boolean>;
    driverRef: MutableRefObject<Driver | null>;
    createTourRef: MutableRefObject<(startIndex: number) => void>;
    handleWalkthroughCompleted: () => void;
}): DriveStep[] {
    const {
        stepCopy,
        popoverTag,
        routeAfterIndex,
        routeBackFromStep,
        theme,
        navigate,
        chainingTourRef,
        driverRef,
        createTourRef,
        handleWalkthroughCompleted,
    } = p;

    return TOUR_SELECTORS.map((element, stepIndex) => {
        const copy = stepCopy[stepIndex];
        if (!copy) throw new Error(`product-exploration tour: missing copy for step ${stepIndex}`);
        const chainRoute = NAVIGATE_AFTER_STEP.has(stepIndex) ? routeAfterIndex[stepIndex] : undefined;
        const backRoute = routeBackFromStep[stepIndex];

        const popover: NonNullable<DriveStep['popover']> = {
            title: ' ',
            description: ' ',
            onPopoverRender: (_popover: unknown, opts: { driver: Driver }) => {
                const customNext =
                    chainRoute !== undefined
                        ? () => {
                              chainingTourRef.current = true;
                              opts.driver.destroy();
                              driverRef.current = null;
                              navigate(chainRoute);
                              window.setTimeout(() => {
                                  createTourRef.current(stepIndex + 1);
                                  chainingTourRef.current = false;
                              }, 550);
                          }
                        : undefined;

                const customPrevious =
                    backRoute !== undefined
                        ? () => {
                              chainingTourRef.current = true;
                              opts.driver.destroy();
                              driverRef.current = null;
                              navigate(backRoute);
                              window.setTimeout(() => {
                                  createTourRef.current(stepIndex - 1);
                                  chainingTourRef.current = false;
                              }, 550);
                          }
                        : undefined;

                renderPopover(
                    opts,
                    copy.title,
                    copy.description,
                    theme,
                    TOTAL_STEPS,
                    popoverTag,
                    customNext,
                    customPrevious,
                    handleWalkthroughCompleted,
                    () => {
                        opts.driver.destroy();
                        driverRef.current = null;
                        navigate(reverse('helpCenter.Home'));
                    }
                );
            },
        };

        return { element, popover };
    });
}

export const useProductExplorationTour = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { i18n } = useTranslation();
    const units = useUnitProvider();
    const country = useSelector(getSessionCountry);
    const cornCatalogBrands = useSelector((state) => getCatalogBrandsByCrop(state, 'corn'));
    const cpCatalogBrands = useSelector((state) => getCatalogBrandsByCrop(state, 'crop_protection'));

    const cornProductsQuery = useCatalogProductsByBrands(
        cornCatalogBrands,
        {
            crop: 'corn',
            country: country?.code || 'AR',
            page: 1,
            pageSize: 10000,
        },
        { enabled: cornCatalogBrands.length > 0 && !!country?.code }
    );

    const cpProductsQuery = useAllCatalogProducts(
        {
            brand: cpCatalogBrands[0]?.name,
            crop: 'crop_protection',
            country: country?.code || '',
            page: 1,
            pageSize: 10000,
        },
        { enabled: !!cpCatalogBrands[0]?.name && !!country?.code }
    );

    const tourPaths: TourPaths = useMemo(() => {
        const landingPath = reverse('productExploration.main');
        const hybridListPath = reverse('productExploration.list', {
            params: { type: 'corn', slug: ALL_BRANDS_SLUG },
        });
        const cpSlug = cpCatalogBrands[0]?.slug || 'bayer';
        const cpListPath = reverse('productExploration.list', {
            params: { type: 'crop_protection', slug: cpSlug },
        });

        const cornRaw = cornProductsQuery.data?.results || [];
        const cornParsed = parseProductForProductCard(
            cornRaw
                .filter((p: { catalogZone?: string }) => Boolean(_(p.catalogZone)))
                .map((p) => ({
                    ...p,
                    catalogType: 'corn' as const,
                    slugLabel: p.brand?.name?.toLowerCase() || '',
                })),
            units
        );
        const firstHybridProduct = cornParsed[0];

        const cpRaw = cpProductsQuery.data?.results || [];
        const cpParsed = parseProductForProductCard(
            cpRaw.map((p) => ({
                ...p,
                catalogType: 'crop_protection' as const,
                slugLabel: (typeof (p as { brand?: string }).brand === 'string'
                    ? (p as { brand: string }).brand
                    : (p as { brand?: { name?: string } }).brand?.name || 'bayer'
                ).toLowerCase(),
            })),
            units
        );
        const firstCpProduct = cpParsed[0];

        const hybridDetailPath =
            firstHybridProduct &&
            reverse('productExploration.detail', {
                params: {
                    type: 'corn',
                    slug: firstHybridProduct.slugLabel,
                    productId: firstHybridProduct.id,
                },
            });

        const cpDetailPath =
            firstCpProduct &&
            reverse('productExploration.detail', {
                params: {
                    type: 'crop_protection',
                    slug: firstCpProduct.slugLabel,
                    productId: firstCpProduct.id,
                },
            });

        return {
            landingPath,
            hybridListPath,
            hybridDetailPath: hybridDetailPath || '',
            cpListPath,
            cpDetailPath: cpDetailPath || '',
            ready: Boolean(hybridDetailPath && cpDetailPath && cornParsed.length && cpParsed.length),
        };
    }, [cornProductsQuery.data?.results, cpProductsQuery.data?.results, cpCatalogBrands[0]?.slug, units]);

    const { stepCopy, popoverTag } = useMemo(() => {
        const stepCopyInner = [
            {
                title: _('Buscá cualquier producto'),
                description: _(
                    'Ingresá el nombre de un híbrido o producto de protección de cultivos para encontrarlo al instante. También podés buscar por tecnología (VT3P, RR2) o por nombre del cultivo.'
                ),
            },
            {
                title: _('Híbridos y protección de cultivos'),
                description: _(
                    'Desde acá accedés a los dos catálogos: híbridos con información técnica comercial y comparación H2H, y productos de protección de cultivos Bayer con filtros por cultivo y diagrama de cuándo aplicarlos.'
                ),
            },
            {
                title: _('Híbridos'),
                description: _(
                    'Cada tarjeta muestra la zona, el ciclo y biotecnología del híbrido. Desde acá podés ver el detalle completo o compararlo con otro híbrido del mercado.'
                ),
            },
            {
                title: _('Detalle del híbrido'),
                description: _(
                    'Navegá entre Información general, Comparación head to head frente a la competencia, características técnicas y perfil defensivo.'
                ),
            },
            {
                title: _('Protección de Cultivos'),
                description: _('Encontrá el nombre, el tipo de producto y el cultivo al cual corresponde.'),
            },
            {
                title: _('Detalle del producto'),
                description: _(
                    'Encontrá información comercial importante para utilizar previo a cada visita, las características y cuáles son las ventanas de aplicación de cada producto con respecto al cultivo.'
                ),
            },
        ];
        return { stepCopy: stepCopyInner, popoverTag: _('PRODUCTOS') };
    }, [i18n.language]);

    const isRv = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();
    const isConsultant = useIsValidatedConsultant();
    const walkthroughEligibleUser = isRv || isAdvisor || isConsultant;
    const isPlaywriteEnvironment = useIsPlaywriteEnvironment();

    const driverRef = useRef<Driver | null>(null);
    const createTourRef = useRef<(startIndex: number) => void>(() => {});
    const chainingTourRef = useRef(false);
    const completedViaFinalizarRef = useRef(false);
    const tourReturnPathRef = useRef<string | null>(null);

    const handleWalkthroughCompleted = useCallback(() => {
        completedViaFinalizarRef.current = true;
        mpTrackEvent('walkthrough_completed', { section: MIXPANEL_SECTION });
    }, []);

    const destroyTour = useCallback(() => {
        driverRef.current?.destroy();
        driverRef.current = null;
    }, []);

    const buildSteps = useCallback(
        (routeAfterIndex: Record<number, string>, routeBackFromStep: Record<number, string>) =>
            buildDriveSteps({
                stepCopy,
                popoverTag,
                routeAfterIndex,
                routeBackFromStep,
                theme,
                navigate,
                chainingTourRef,
                driverRef,
                createTourRef,
                handleWalkthroughCompleted,
            }),
        [handleWalkthroughCompleted, navigate, popoverTag, stepCopy, theme]
    );

    const createTourFromStep = useCallback(
        (startIndex: number) => {
            if (isPlaywriteEnvironment || !tourPaths.ready || !walkthroughEligibleUser) return;

            destroyTour();

            if (startIndex === 0 && !chainingTourRef.current) {
                completedViaFinalizarRef.current = false;
                mpTrackEvent('walkthrough_started', { section: MIXPANEL_SECTION });
                const storedReturn = sessionStorage.getItem(PRODUCT_EXPLORATION_TOUR_RETURN_STORAGE_KEY);
                if (storedReturn) {
                    tourReturnPathRef.current = storedReturn;
                    sessionStorage.removeItem(PRODUCT_EXPLORATION_TOUR_RETURN_STORAGE_KEY);
                } else {
                    tourReturnPathRef.current = `${location.pathname}${location.search}${location.hash}`;
                }
            }

            const routeAfterIndex: Record<number, string> = {
                1: tourPaths.hybridListPath,
                2: tourPaths.hybridDetailPath,
                3: tourPaths.cpListPath,
                4: tourPaths.cpDetailPath,
            };

            const routeBackFromStep: Record<number, string> = {
                2: tourPaths.landingPath,
                3: tourPaths.hybridListPath,
                4: tourPaths.hybridDetailPath,
                5: tourPaths.cpListPath,
            };

            const steps = buildSteps(routeAfterIndex, routeBackFromStep);

            const d = driver({
                showProgress: true,
                stagePadding: 0,
                steps: steps.map((step) => ({
                    element: step.element,
                    popover: step.popover,
                })),
                allowClose: true,
                overlayClickBehavior: 'close',
                disableActiveInteraction: true,
                animate: true,
                onDestroyed: (_element, _step, opts) => {
                    driverRef.current = null;
                    if (chainingTourRef.current) return;

                    const finishedWithFinalizar = completedViaFinalizarRef.current;

                    if (!finishedWithFinalizar) {
                        try {
                            if (!opts.driver.isLastStep()) {
                                mpTrackEvent('walkthrough_dismissed', { section: MIXPANEL_SECTION });
                            }
                        } catch {
                            mpTrackEvent('walkthrough_dismissed', { section: MIXPANEL_SECTION });
                        }
                    }

                    if (finishedWithFinalizar && tourReturnPathRef.current) {
                        navigate(tourReturnPathRef.current, { replace: true });
                    }
                    tourReturnPathRef.current = null;
                    completedViaFinalizarRef.current = false;
                    localStorage.setItem(PRODUCT_EXPLORATION_TOUR_STORAGE_KEY, 'true');
                },
                onCloseClick: (_element, _step, opts) => {
                    localStorage.setItem(PRODUCT_EXPLORATION_TOUR_STORAGE_KEY, 'true');
                    opts.driver.destroy();
                    driverRef.current = null;
                },
            });

            driverRef.current = d;
            d.drive(startIndex);
        },
        [
            buildSteps,
            destroyTour,
            isPlaywriteEnvironment,
            location.hash,
            location.pathname,
            location.search,
            navigate,
            tourPaths,
            walkthroughEligibleUser,
        ]
    );

    useEffect(() => {
        createTourRef.current = createTourFromStep;
    }, [createTourFromStep]);

    const startTourFromLanding = useCallback(() => {
        if (isPlaywriteEnvironment || !tourPaths.ready || !walkthroughEligibleUser) return;
        sessionStorage.removeItem(PRODUCT_EXPLORATION_TOUR_RETURN_STORAGE_KEY);
        window.setTimeout(() => createTourFromStep(0), 350);
    }, [createTourFromStep, isPlaywriteEnvironment, tourPaths.ready, walkthroughEligibleUser]);

    const requestTourFromAnyRoute = useCallback(() => {
        if (!walkthroughEligibleUser) return;
        const isLanding = /^\/product-exploration\/?$/.test(location.pathname);
        if (!isLanding) {
            sessionStorage.setItem(
                PRODUCT_EXPLORATION_TOUR_RETURN_STORAGE_KEY,
                `${location.pathname}${location.search}${location.hash}`
            );
            navigate(reverse('productExploration.main'), { state: { productExplorationTour: true } });
            return;
        }
        startTourFromLanding();
    }, [location.hash, location.pathname, location.search, navigate, startTourFromLanding, walkthroughEligibleUser]);

    const pendingLandingTourRef = useRef(false);

    useEffect(() => {
        const state = location.state as { productExplorationTour?: boolean } | null;
        if (!state?.productExplorationTour) return;
        if (walkthroughEligibleUser) {
            pendingLandingTourRef.current = true;
        }
        navigate(
            { pathname: location.pathname, search: location.search, hash: location.hash },
            { replace: true, state: {} }
        );
    }, [location.hash, location.pathname, location.search, location.state, navigate, walkthroughEligibleUser]);

    useEffect(() => {
        if (!pendingLandingTourRef.current || !tourPaths.ready || isPlaywriteEnvironment || !walkthroughEligibleUser) {
            return;
        }
        pendingLandingTourRef.current = false;
        window.setTimeout(() => createTourFromStep(0), 450);
    }, [createTourFromStep, isPlaywriteEnvironment, tourPaths.ready, walkthroughEligibleUser]);

    return {
        startTour: requestTourFromAnyRoute,
        startTourFromLanding,
        tourPathsReady: tourPaths.ready && walkthroughEligibleUser,
    };
};
